import UserDataSource from "data/UserDataSource";

declare var $:any;

import { observable, action, computed } from 'mobx';
import { GET_QUERY, POST_QUERY, POSTMULTIPART_QUERY } from 'utils';
import { UIState } from './UIState';
import * as moment from 'moment';
import { TrackersData, ITrackersWeekSummary } from './TrackersData';

const ENTRY_SOURCE = {
  JOURNAL:'journal',
  PRACTITIONER:'practitioner',
  LONG_TERM_GOAL:'long_term_goal',
  SHORT_TERM_GOAL:'short_term_goal'
};



interface IJournalEntry {
  _id: string;
  createdDate: Date;
  userId: string;
  photo: string;
  text: string;
  mood: string; 
  source: string;  
}

class JournalLine {
  headline:string;
  entry:IJournalEntry;
  trackersSummary:ITrackersWeekSummary;

  @computed
  get createdDate():Date {
    if(this.entry) {
      return this.entry.createdDate;
    } else {
      return this.trackersSummary.isThisWeek ?  moment().startOf('day').toDate() : this.trackersSummary.lastDay.toDate();
    }
  }
}

function journalLineForJournalEntry(entry:IJournalEntry) {
  let journalLine = new JournalLine();
  journalLine.entry = entry;    
  return journalLine;
}

function journalLineForTrackersSummary(trackersSummary:ITrackersWeekSummary) {
  let journalLine = new JournalLine();
  journalLine.trackersSummary = trackersSummary;      
  return journalLine;
}





const MOOD = {
  VERY_SATISFIED:'sentiment_very_satisfied',
  SATISFIED:'sentiment_satisfied',
  DISSATISFIED:'sentiment_dissatisfied',
  VERY_DISSATISFIED:'sentiment_very_dissatisfied',
};

const MOOD_LIST = [
  MOOD.VERY_SATISFIED, 
  MOOD.SATISFIED, 
  MOOD.DISSATISFIED, 
  MOOD.VERY_DISSATISFIED, 
]


class _JournalData {
  @observable loaded:boolean = false;
  @observable loading:boolean = false;
  @observable noMoreJournalEntries:boolean = false;

  @observable private _journalEntries:Array<IJournalEntry> = [];

  @observable oldestJournalEntry:Date = new Date();

  @computed
  get journalEntries() {
    return this._journalEntries;
  }

  @computed 
  get journalEntryLines() {
    const journalLines:Array<JournalLine> = [];
    this._journalEntries.forEach(entry=>{ journalLines.push(journalLineForJournalEntry(entry)); })
    return journalLines;
  }

  @computed
  get journalTrackersLines() {
    const journalLines:Array<JournalLine> = [];
    TrackersData.trackersSummaries.forEach(summary=>{ journalLines.push(journalLineForTrackersSummary(summary)); })
    return journalLines;    
  }

  @computed
  get journalLines() {
    const journalLines:Array<JournalLine> = [];
    for(let i=0;i<this.journalEntryLines.length;i++) {
      journalLines.push(this.journalEntryLines[i]);
    }

    const currentEarliestWeek =  this.noMoreJournalEntries ? TrackersData.firstWeeklyMoment : (this._journalEntries && this._journalEntries.length>0 ? moment(this._journalEntries[this._journalEntries.length-1].createdDate).startOf('week') : moment().startOf('week'));

    for(let i=0;i<this.journalTrackersLines.length;i++) {
      if(this.journalTrackersLines[i].trackersSummary.startDay.isBefore(currentEarliestWeek)) {
        break;
      } else {
        journalLines.push(this.journalTrackersLines[i]);
      }
    }
    journalLines.sort((t1,t2)=> (moment(t1.createdDate).isBefore(moment(t2.createdDate)) ? 1 : -1 ));
    return journalLines;
  }
  
  @action
	load = (olderThanDate?:Date|null) => new Promise<void>((resolve, reject)=>{
    if(UIState.settings.ENABLE_JOURNAL && !this.loading) {
      this.loading = true;
      const payload = olderThanDate ? { olderThanDate: olderThanDate } : {}; 
      $.ajax(POST_QUERY('/journal/journalEntries', payload))
      .done((results:Array<IJournalEntry>)=>{        
        this.loaded = true;
        if(results.length>0) { 
          for(let i=0;i<results.length;i++) {
            this._journalEntries.push(results[i]);
          }
        } else {
          this.noMoreJournalEntries = true;
        }      
        this.loading = false;
        resolve();
      })
      .fail((err)=>{
        this.loading = false;
        reject(err);
      });
    } else {
      this.noMoreJournalEntries = true;
    }
  });

  @action
  loadMore = ():Promise<any> => {
    const olderThanDate = this._journalEntries.length>0 ? this._journalEntries[this._journalEntries.length-1].createdDate : null; 
    return this.load(olderThanDate);
  }

  @action
  createJournalEntry = (text, mood, photo) => new Promise<void>((resolve, reject)=>{
    const formData = new FormData();
		if(text) { formData.append("text", text); }
		if(mood) { formData.append("mood", mood); }
    if(photo) {
      const photoName = `journal_${UIState.user._id}_${moment().format('YYYY-MM-DD-HH-mm-ss')}.jpg`;
			formData.append("file", photo, photoName);
      formData.append("photo", photoName);
		}	
		
    $.ajax(POSTMULTIPART_QUERY('/journal/createJournalEntry', formData))
	  .done(journalEntry => {
      this._journalEntries.splice(0,0,journalEntry);      
      resolve();
	  })
	  .fail((error)=>{
      reject(error);
    });	
  });

  @action
  createGoalCompleteJournalEntry = (text, shortTeam:boolean) => new Promise<void>((resolve, reject)=>{
    const formData = new FormData();
		formData.append("text", text);
    formData.append("source", shortTeam?ENTRY_SOURCE.SHORT_TERM_GOAL:ENTRY_SOURCE.LONG_TERM_GOAL);
		
    $.ajax(POSTMULTIPART_QUERY('/journal/createJournalEntry', formData))
	  .done(journalEntry => {
      this._journalEntries.splice(0,0,journalEntry);      
      resolve();
	  })
	  .fail((error)=>{
      reject(error);
    });	
  });


  @action
  deleteJournalEntry = (journalEntry:IJournalEntry) => new Promise<void>((resolve, reject)=>{
    $.ajax(GET_QUERY(`/journal/deleteJournalEntry/${journalEntry._id}`))
    .done(()=>{
      const idx = this._journalEntries.findIndex(t=>t._id===journalEntry._id);
      if(-1!=idx) {
        this._journalEntries.splice(idx, 1);
      }
      resolve();
    })
    .fail((error)=>{
      reject(error);
    });
  });




}



const JournalData = new _JournalData();
export { JournalData, IJournalEntry, MOOD, MOOD_LIST, ENTRY_SOURCE, JournalLine }
